/**
 * Product Controller
 *
 * Custom element that wraps Shopify product forms and handles variant selection.
 * Manages product options, variant availability, and updates form inputs.
 * Other controllers (cart, quick-view, wishlist) read data from this controller.
 *
 * Architecture:
 * - Custom element wrapping product form
 * - Scoped event delegation within product form
 * - Updates hidden inputs for other controllers to read
 * - Emits events for external listeners
 *
 * Features:
 * - Variant option selection and switching
 * - Variant availability checking
 * - Dynamic price updates
 * - Image switching on variant change
 * - Quantity management (increase/decrease)
 * - Updates hidden form inputs for cart/quick-view
 *
 * Events Emitted:
 * - product:variant-changed { variant, selectedOptions, productId } - When variant changes
 *
 * Data Attributes Required:
 * - [data-variants] - Container with variants JSON (on product-controller element)
 * - [data-product-id] - Product ID (on product-controller element)
 * - [data-show-color-variant-image] - Boolean to show variant image on color change (on product-controller element)
 * - [data-option-index] - Option index number (on fieldset elements)
 * - [data-qty-increase] - Quantity increase button
 * - [data-qty-decrease] - Quantity decrease button
 * - [data-product-image] - Product image element to update
 *
 * Form Elements:
 * - input[name="id"] - Hidden variant ID input (Shopify standard, read by cart/quick-view)
 * - input[name="quantity"] - Quantity input (read by cart controller)
 * - input[type="radio"][name^="option"] - Variant option radio buttons
 *
 * Integration:
 * - Cart controller reads: input[name="id"] and input[name="quantity"]
 * - Quick-view controller reads: input[name="id"]
 * - Wishlist reads: data-product-id attribute
 *
 * Locale Strings Used:
 * - window.themeStrings.cart.sold_out - "Sold Out" button text
 *
 * Usage:
 * Wrap product forms with <product-controller> custom element.
 * Controller automatically initializes and manages variant selection.
 */

class ProductController extends HTMLElement {
  constructor() {
    super();

    this.productId = null;
    this.variants = [];
    this.selectedOptions = [];

    // Save bound references for proper cleanup
    this._boundHandleChange = this._handleDelegatedChange.bind(this);
    this._boundHandleClick = this._handleDelegatedClick.bind(this);
    this._boundHandleInput = this._handleDelegatedInput.bind(this);
  }

  connectedCallback() {
    this._loadProductData();

    // Event delegation
    this.addEventListener("change", this._boundHandleChange);
    this.addEventListener("click", this._boundHandleClick);
    this.addEventListener("input", this._boundHandleInput);
  }

  disconnectedCallback() {
    this.removeEventListener("change", this._boundHandleChange);
    this.removeEventListener("click", this._boundHandleClick);
    this.removeEventListener("input", this._boundHandleInput);
  }

  /* ===========================================
   * DATA LOADING
   * =========================================== */

  _loadProductData() {
    try {
      this.variants = JSON.parse(this.dataset.variants || "[]");
      this.productId = this.dataset.productId || null;

      // Initialize selected options from current state
      this._initializeSelectedOptions();

      // Find and set current variant
      const currentVariant = this._findVariant(this.selectedOptions);

      // Update option availability indicators on initial load
      this._updateOptionAvailability();

      // Update UI to match current state
      this._updateVariantUI(currentVariant);

      // Update wishlist button state
      this._updateWishlistButtonState();
    } catch (error) {
      console.error("Failed to load product data:", error);
    }
  }

  _initializeSelectedOptions() {
    const fieldsets = this.querySelectorAll("fieldset[data-option-index]");
    this.selectedOptions = Array.from(fieldsets).map((fieldset) => {
      const checked = fieldset.querySelector('input[type="radio"]:checked');
      return checked ? checked.value : null;
    });
  }

  /* ===========================================
   * EVENT DELEGATION
   * =========================================== */

  _handleDelegatedChange(e) {
    const target = e.target;

    // Variant option selection
    if (target.matches('input[type="radio"][name^="option"]')) {
      this._onVariantChange(e);
    }
  }

  _handleDelegatedClick(e) {
    const target = e.target.closest("button, a");
    if (!target) return;

    // Quantity buttons
    if (target.matches("[data-qty-increase]")) {
      e.preventDefault();
      this._changeQuantity(1);
    } else if (target.matches("[data-qty-decrease]")) {
      e.preventDefault();
      this._changeQuantity(-1);
    }
  }

  _handleDelegatedInput(e) {
    const target = e.target;

    // Quantity input
    if (target.matches('input[name="quantity"]')) {
      this._onQuantityChange(e);
    }
  }

  /* ===========================================
   * VARIANT MANAGEMENT
   * =========================================== */

  _onVariantChange(e) {
    const input = e.target;
    const optionIndex = parseInt(input.closest("fieldset").dataset.optionIndex);

    this.selectedOptions[optionIndex] = input.value;

    const currentVariant = this._findVariant(this.selectedOptions);

    this._updateOptionAvailability();

    this._updateVariantUI(currentVariant);

    // Emit event
    Shopify.emitEvent("product:variant-changed", {
      variant: currentVariant,
      selectedOptions: this.selectedOptions,
      productId: this.productId,
    });
  }

  _findVariant(selectedOptions) {
    // Try to find available variant first
    let variant = this.variants.find(
      (v) =>
        v.available && v.options.every((opt, i) => opt === selectedOptions[i]),
    );

    // If no available variant, find any matching variant
    if (!variant) {
      variant = this.variants.find((v) =>
        v.options.every((opt, i) => opt === selectedOptions[i]),
      );
    }

    return variant || null;
  }

  _updateOptionAvailability() {
    const fieldsets = this.querySelectorAll("fieldset[data-option-index]");

    fieldsets.forEach((fieldset, fieldsetIndex) => {
      fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
        const testOptions = [...this.selectedOptions];
        testOptions[fieldsetIndex] = input.value;

        // Check if any variant exists with this option combination
        const isAvailable = this.variants.some(
          (v) =>
            v.available && v.options.every((opt, i) => opt === testOptions[i]),
        );

        // Update UI indicator
        const label = fieldset.querySelector(`label[for="${input.id}"]`);
        if (label) {
          const indicator = label.querySelector("[data-unavailable-indicator]");
          if (indicator) {
            indicator.style.display = isAvailable ? "none" : "block";
          }

          label.classList.toggle("unavailable", !isAvailable);
        }

        // Disable input if not available
        // input.disabled = !isAvailable;
      });
    });
  }

  _updateVariantUI(variant) {
    if (!variant) {
      this._updateAddToCartButtons(null);
      return;
    }

    this._updateVariantInput(variant);

    this._updatePrice(variant);

    this._updateImage(variant);

    this._updateAddToCartButtons(variant);
  }

  _updateVariantInput(variant) {
    // Update hidden input with name="id" (Shopify standard)
    const variantInput = this.querySelector('input[name="id"]');
    if (variantInput) {
      variantInput.value = variant.id;
    }
  }

  _updatePrice(variant) {
    // Price
    const priceEl = this.querySelector("[data-product-price]");
    if (priceEl && variant.price) {
      priceEl.textContent = Shopify.formatMoney(variant.price);
    }

    // Compare at price
    const compareEl = this.querySelector("[data-product-compare-price]");
    if (compareEl) {
      if (
        variant.compare_at_price &&
        variant.compare_at_price > variant.price
      ) {
        compareEl.textContent = Shopify.formatMoney(variant.compare_at_price);
        compareEl.hidden = false;
      } else {
        compareEl.hidden = true;
      }
    }
  }

  _updateImage(variant) {
    const imageEls = this.querySelectorAll("[data-product-image]");
    const showColorVariantImage = this.dataset.showColorVariantImage === "true";

    if (
      imageEls.length > 0 &&
      variant.featured_image &&
      showColorVariantImage
    ) {
      imageEls.forEach((imageEl) => {
        imageEl.src = variant.featured_image.src;
        imageEl.alt = variant.featured_image.alt || variant.name;

        // Update srcset if needed
        if (imageEl.hasAttribute("srcset")) {
          imageEl.srcset = `
            ${variant.featured_image.src}&width=300 300w,
            ${variant.featured_image.src}&width=600 600w,
            ${variant.featured_image.src}&width=900 900w
          `.trim();
        }
      });
    }
  }

  _updateAddToCartButtons(variant) {
    this.querySelectorAll("[data-add-to-cart]").forEach((btn) => {
      const isBuyNow = btn.dataset.buynow === "true";

      if (!variant || !variant.available) {
        btn.disabled = true;
        btn.textContent = window.themeStrings?.cart?.sold_out || "Sold Out";
      } else {
        btn.disabled = false;
        btn.dataset.state = "idle";
        btn.dataset.variantId = variant.id;

        if (isBuyNow) {
          btn.textContent = window.themeStrings?.cart?.buy_now || "Buy Now";
        } else {
          btn.textContent =
            window.themeStrings?.cart?.add_to_cart || "Add to Cart";
        }
      }
    });
  }

  /* ===========================================
   * QUANTITY MANAGEMENT
   * =========================================== */

  _onQuantityChange(e) {
    const input = e.target;
    let value = parseInt(input.value, 10);

    if (isNaN(value) || value < 1) {
      value = 1;
    }

    // Sync all quantity inputs in this product
    this.querySelectorAll('input[name="quantity"]').forEach((qtyInput) => {
      qtyInput.value = value;
    });

    // Update decrease button state
    this._updateQuantityButtons(value);
  }

  _changeQuantity(delta) {
    const input = this.querySelector('input[name="quantity"]');
    if (!input) return;

    let value = parseInt(input.value, 10) || 1;
    value += delta;

    if (value < 1) value = 1;

    input.value = value;
    input.dispatchEvent(new Event("input", { bubbles: true }));

    // Update decrease button state
    this._updateQuantityButtons(value);
  }

  _updateQuantityButtons(value) {
    const minQty = 1;
    const decreaseBtn = this.querySelector("[data-qty-decrease]");

    if (decreaseBtn) {
      decreaseBtn.disabled = value <= minQty;
    }
  }

  /* ===========================================
   * Wishlist State Management
   * =========================================== */
  _updateWishlistButtonState() {
    if (!this.productId) return;

    const wishlistController = document.querySelector("wishlist-controller");
    if (!wishlistController) return;

    const isInWishlist = wishlistController.has(String(this.productId));
    wishlistController._updateButton(this.productId, isInWishlist);
  }
}

customElements.define("product-controller", ProductController);
